import store from "@/store";
import { transform } from "ol/proj";

const RECORDS_PER_PAGE = 10;
const MAX_DOWNLOAD_RECORDS = 10000;

export const attributesTableService = {
    // ... existing methods keep working ...

    async downloadFeatures(layer, filters, spatialFilter, format) {
        // Enviar notificación de inicio
        store.dispatch("toast/show", {
            message: "Iniciando descarga...",
            type: "info"
        });

        try {
            const provider = layer.provider.name;
            let features = [];
            let totalCount = 0;

            if (provider === "geoserver") {
                // Fetch all matching features in one go (up to limit)
                const result = await this.getAttributesGeoserver(layer, 0, filters, spatialFilter, MAX_DOWNLOAD_RECORDS);
                features = result.features;
                totalCount = result.total;
            } else if (provider === "arcgis") {
                // Fetch in batches if necessary
                let currentOffset = 0;
                let hasMore = true;
                const batchSize = 2000;

                while (hasMore && features.length < MAX_DOWNLOAD_RECORDS) {
                    const result = await this.getAttributesArcgis(layer, currentOffset, filters, spatialFilter, batchSize);
                    features = features.concat(result.features);
                    totalCount = result.total;
                    currentOffset += batchSize;
                    
                    // Stop if we got less than requested or reached limit
                    hasMore = result.features.length === batchSize && features.length < MAX_DOWNLOAD_RECORDS;
                }
            }

            if (features.length === 0) {
                store.dispatch("toast/show", { message: "No hay datos para descargar", type: "warning" });
                return;
            }

            // Slice to limit just in case
            const dataToDownload = features.slice(0, MAX_DOWNLOAD_RECORDS);
            
            if (totalCount > MAX_DOWNLOAD_RECORDS) {
                store.dispatch("toast/show", { 
                    message: `Descarga limitada a los primeros ${MAX_DOWNLOAD_RECORDS} registros`, 
                    type: "warning" 
                });
            }

            const filename = `${layer.name.replace(/\s+/g, '_')}_${new Date().getTime()}`;

            if (format === "csv") {
                const headers = Object.keys(dataToDownload[0]).filter(k => k !== 'bbox' && k !== 'geometry');
                const csvContent = this.jsonToCsv(dataToDownload, headers);
                this.triggerDownload(`${filename}.csv`, csvContent, "text/csv;charset=utf-8;");
            } else if (format === "geojson") {
                const geojson = this.toGeoJSON(dataToDownload, layer);
                this.triggerDownload(`${filename}.geojson`, JSON.stringify(geojson), "application/json;charset=utf-8;");
            }

            store.dispatch("toast/show", { message: "Descarga completada", type: "success" });

        } catch (error) {
            console.error("Error downloading features:", error);
            store.dispatch("toast/show", { message: "Error al descargar los datos", type: "error" });
        }
    },

    jsonToCsv(items, headers) {
        const replacer = (key, value) => (value === null ? "" : value);
        const csv = [
            headers.join(","),
            ...items.map(row =>
                headers
                    .map(fieldName => {
                        const val = row[fieldName];
                        // Escape quotes and wrap in quotes
                        const stringVal = val === undefined || val === null ? "" : String(val);
                        return `"${stringVal.replace(/"/g, '""')}"`;
                    })
                    .join(",")
            )
        ].join("\r\n");
        return csv;
    },

    transformGeometry(geometry, from, to) {
        if (!geometry) return null;
        try {
            const transformed = JSON.parse(JSON.stringify(geometry));
            if (transformed.type === "Point") {
                transformed.coordinates = transform(transformed.coordinates, from, to);
            } else if (transformed.type === "LineString" || transformed.type === "MultiPoint") {
                transformed.coordinates = transformed.coordinates.map(c => transform(c, from, to));
            } else if (transformed.type === "Polygon" || transformed.type === "MultiLineString") {
                transformed.coordinates = transformed.coordinates.map(r => r.map(c => transform(c, from, to)));
            } else if (transformed.type === "MultiPolygon") {
                transformed.coordinates = transformed.coordinates.map(p => p.map(r => r.map(c => transform(c, from, to))));
            }
            return transformed;
        } catch (e) {
            console.error("Error transforming geometry", e);
            return geometry;
        }
    },

    toGeoJSON(features, layer) {
        return {
            type: "FeatureCollection",
            features: features.map(f => {
                const props = { ...f };
                delete props.bbox;
                delete props.geometry;

                return {
                    type: "Feature",
                    geometry: this.transformGeometry(f.geometry, "EPSG:3857", "EPSG:4326"),
                    properties: props
                };
            })
        };
    },

    triggerDownload(filename, content, mimeType) {
        const blob = new Blob([content], { type: mimeType });
        if (navigator.msSaveBlob) { // IE 10+
            navigator.msSaveBlob(blob, filename);
        } else {
            const link = document.createElement("a");
            if (link.download !== undefined) {
                const url = URL.createObjectURL(blob);
                link.setAttribute("href", url);
                link.setAttribute("download", filename);
                link.style.visibility = "hidden";
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }
        }
    },

    async openTable(layer, spatialFilter = null) {
        // 1️⃣ Activar la herramienta
        store.dispatch("setMapToolSelection", {
            tool: "attributesTable",
            active: true
        });

        // 2️⃣ Obtener los atributos (primera página, sin filtros)
        const { features, total } = await this.fetchAttributes(layer, 0, {}, spatialFilter);

        // 3️⃣ Enviar datos al módulo attributeTable
        store.dispatch("attributeTable/openTable", {
            layer,
            attributes: features,
            totalFeatures: total
        });

        // 4️⃣ Si hay filtro espacial, registrarlo en el store
        if (spatialFilter) {
            store.dispatch("attributeTable/setSpatialFilter", spatialFilter);
        }
    },

    async updateTable(layer, offset, filters, spatialFilter = null) {
        const { features, total } = await this.fetchAttributes(layer, offset, filters, spatialFilter);
        console.log("updateTable", features, total);
        store.dispatch("attributeTable/updateAttributes", {
            attributes: features,
            totalFeatures: total
        });
    },
    generateCQLFilter(filters) {
        const filters_query = Object.entries(filters)
            .filter(([_, v]) => v && v.trim() !== "")
            .map(([key, val]) => {
                const cleanVal = val.replace(/[\t\n\r]+/g, " ").trim(); // elimina tabs y saltos de línea
                return `${key} ILIKE '%${cleanVal.replace(/'/g, "''")}%'`; // escapa comillas simples
            })
            .join(" AND ") || "INCLUDE";

        console.log("🧩 CQL_FILTER generado:", filters_query);
        return filters_query;
    },

    generateBboxCQL(extent) {
        if (!extent || extent.length !== 4) return null;
        const [xMin, yMin, xMax, yMax] = extent;
        return `BBOX(the_geom,${xMin},${yMin},${xMax},${yMax},'EPSG:3857')`;
    },
    async getAttributesGeoserver(layer, offset, filters, spatialFilter = null, count = RECORDS_PER_PAGE) {
        const baseUrl = layer.provider.url;
        const layername = `${layer.provider.geoserverdata.workspace}:${layer.provider.geoserverdata.datastore}`;

        let url =
            `${baseUrl}/wfs?service=WFS` +
            `&version=2.0.0` + // ✅ usa la versión moderna de WFS
            `&request=GetFeature` +
            `&typeNames=${layername}` + // ✅ plural correcto
            `&outputFormat=application/json` +
            `&srsName=EPSG:3857` + // ✅ reproyección al vuelo a Web Mercator
            `&count=${count}` +
            `&startIndex=${offset}`

        // ── Build combined CQL filter (column filters AND spatial filter) ──
        const cqlParts = [];

        if (filters) {
            const columnCql = this.generateCQLFilter(filters);
            if (columnCql && columnCql !== "INCLUDE") {
                cqlParts.push(columnCql);
            }
        }

        if (spatialFilter) {
            const bboxCql = this.generateBboxCQL(spatialFilter);
            if (bboxCql) {
                cqlParts.push(bboxCql);
            }
        }

        if (cqlParts.length > 0) {
            const combinedCql = cqlParts.join(" AND ");
            url = `${url}&CQL_FILTER=${encodeURIComponent(combinedCql)}`;
        }
        // ── END combined filter ──

        console.log("Fetching attributes from Geoserver:", url);
        const response = await fetch(url)
            .then(res => res.json())
            .then(data => {
                console.log("Geoserver response:", data);
                const features = []
                data.features.forEach(feature => {
                    features.push({
                        ...feature.properties,
                        geometry: feature.geometry,
                        bbox: feature.bbox
                    })
                })
                return { features, total: data.totalFeatures };
            })
            .catch(err => console.error(err));
        return response;
    },


    generateArcGISWhereClause(filters) {
        if (!filters || Object.keys(filters).length === 0) return "1=1";

        return Object.entries(filters)
            .filter(([_, v]) => v && v.trim() !== "")
            .map(([key, val]) => {
                const cleanVal = val.replace(/[\t\n\r]+/g, " ").trim();
                // Check if it looks like a number, otherwise treat as string
                // Note: IsNaN works for checking if string is NOT a number.
                if (isNaN(cleanVal)) {
                    return `UPPER(${key}) LIKE UPPER('%${cleanVal.replace(/'/g, "''")}%')`;
                } else {
                    // It's a number (or string number), so we can try exact match or keep LIKE depending on preference.
                    // The snippet used exact match for numbers.
                    return `${key} = ${cleanVal}`;
                }
            })
            .join(" AND ") || "1=1";
    },

    /**
     * Builds WMS/ArcGIS filter params from column + spatial filters.
     *
     * GeoServer → { cqlFilter: String|null }
     *   combines column ILIKE and bbox CQL, passes to TileWMS.updateParams({ CQL_FILTER })
     *
     * ArcGIS MapServer → { layerDefs: String|null }
     *   column WHERE clause only. Spatial bbox is NOT applied to WMS tiles —
     *   ArcGIS MapServer /export does not support geometry filter params.
     *
     * @param {Object} layer         — layer config from Vuex activeLayers
     * @param {Object} columnFilters — { [columnName]: String }
     * @param {Array|null} spatialFilter — [xMin, yMin, xMax, yMax] EPSG:3857 or null
     * @returns {{ cqlFilter?: String|null, layerDefs?: String|null }}
     */
    buildWMSFilterParams(layer, columnFilters, spatialFilter) {
        const provider = layer.provider.name;

        if (provider === 'geoserver') {
            const cqlParts = [];
            const columnCql = this.generateCQLFilter(columnFilters || {});
            if (columnCql && columnCql !== 'INCLUDE') {
                cqlParts.push(columnCql);
            }
            if (spatialFilter) {
                const bboxCql = this.generateBboxCQL(spatialFilter);
                if (bboxCql) cqlParts.push(bboxCql);
            }
            return { cqlFilter: cqlParts.length > 0 ? cqlParts.join(' AND ') : null };
        }

        if (provider === 'arcgis') {
            const whereClause = this.generateArcGISWhereClause(columnFilters || {});
            const hasFilters = whereClause && whereClause !== '1=1';
            return {
                layerDefs: hasFilters
                    ? JSON.stringify({ [layer.codigo]: whereClause })
                    : null
            };
        }

        return {};
    },

    async getAttributesArcgis(layer, offset, filters, spatialFilter = null, count = RECORDS_PER_PAGE) {
        const baseUrl = layer.provider.url;
        const whereClause = this.generateArcGISWhereClause(filters);
        const layerCodigo = layer.codigo;
        let url = ""
        url = `${baseUrl}/${layerCodigo}/query?`
        if (baseUrl.toLowerCase().includes("imageserver")) {
            url = `${baseUrl}/query?`
        }

        // Construct URL
        url = url +
            `where=${encodeURIComponent(whereClause)}` +
            `&outFields=*` +
            `&returnGeometry=true` +
            `&outSR=3857` +
            `&resultOffset=${offset}` +
            `&resultRecordCount=${count}` +
            `&f=json`;

        // ── NEW: Add spatial filter for ArcGIS ──
        if (spatialFilter && spatialFilter.length === 4) {
            const [xMin, yMin, xMax, yMax] = spatialFilter;
            url += `&geometry=${xMin},${yMin},${xMax},${yMax}`;
            url += `&geometryType=esriGeometryEnvelope`;
            url += `&inSR=3857`;
            url += `&spatialRel=esriSpatialRelIntersects`;
        }
        // ── END NEW ──

        console.log("Fetching attributes from ArcGIS:", url);

        try {
            const response = await fetch(url).then(res => res.json());

            if (response.error) {
                console.error("ArcGIS Error:", response.error);
                return { features: [], total: 0 };
            }

            const features = response.features.map(f => {
                const attributes = f.attributes || {};
                let bbox = null;
                let geojsonGeometry = null;

                if (f.geometry) {
                    // Logic to calculate bbox and convert to GeoJSON geometry
                    let xMin = Infinity, yMin = Infinity, xMax = -Infinity, yMax = -Infinity;
                    let hasCoords = false;

                    const processRing = (ring) => {
                        ring.forEach(coord => {
                            const x = coord[0];
                            const y = coord[1];
                            if (x < xMin) xMin = x;
                            if (y < yMin) yMin = y;
                            if (x > xMax) xMax = x;
                            if (y > yMax) yMax = y;
                            hasCoords = true;
                        });
                    };

                    if (f.geometry.rings) { // Polygon
                        f.geometry.rings.forEach(processRing);
                        geojsonGeometry = { type: "Polygon", coordinates: f.geometry.rings };
                    } else if (f.geometry.paths) { // Polyline
                        f.geometry.paths.forEach(processRing);
                        geojsonGeometry = { type: "LineString", coordinates: f.geometry.paths[0] }; // Simple LineString assume for now
                    } else if (f.geometry.x !== undefined && f.geometry.y !== undefined) { // Point
                        const buffer = 100;
                        xMin = f.geometry.x - buffer;
                        yMin = f.geometry.y - buffer;
                        xMax = f.geometry.x + buffer;
                        yMax = f.geometry.y + buffer;
                        hasCoords = true;
                        geojsonGeometry = { type: "Point", coordinates: [f.geometry.x, f.geometry.y] };
                    }

                    if (hasCoords) {
                        bbox = [xMin, yMin, xMax, yMax];
                    }
                }

                return {
                    ...attributes,
                    geometry: geojsonGeometry,
                    bbox: bbox
                };
            });

            let total = offset + features.length;
            if (response.exceededTransferLimit) {
                total += 1; // Indicate there's at least one more page
            }

            return { features, total };

        } catch (err) {
            console.error("Error fetching ArcGIS attributes:", err);
            return { features: [], total: 0 };
        }
    },
    async fetchAttributes(layer, offset = 0, filters = {}, spatialFilter = null) {
        const provider = layer.provider.name;
        if (provider === "geoserver") {
            return this.getAttributesGeoserver(layer, offset, filters, spatialFilter);
        }
        if (provider === "arcgis") {
            return this.getAttributesArcgis(layer, offset, filters, spatialFilter);
        }

    }
};

